import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Unlock, Lock, AlertTriangle, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

function StatBadge({ label, value }) {
    return (
        <div>
            <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>{label}</div>
            <div className="mt-1 text-sm font-medium" style={{ color: "var(--st-navy)" }}>{value ?? "—"}</div>
        </div>
    );
}

export default function AdminStudentDetail() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [detail, setDetail] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [notice, setNotice] = useState(null);

    // Cancel dialog
    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelReason, setCancelReason] = useState("");

    // Force refund dialog
    const [refundOpen, setRefundOpen] = useState(false);
    const [refundReason, setRefundReason] = useState("");
    const [refundConfirmWord, setRefundConfirmWord] = useState("");

    // Phase unlock dialog
    const [phaseDialog, setPhaseDialog] = useState(null); // { phase_id, name, action }
    const [phaseReason, setPhaseReason] = useState("");

    async function load() {
        setErr(null);
        try {
            const d = await api.adminStudentDetail(userId);
            setDetail(d);
        } catch (e) {
            setErr(e.message);
        }
    }

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

    async function handleCancel() {
        setBusy(true); setErr(null);
        try {
            await api.adminCancelSubscription(userId, cancelReason);
            setNotice("Subscription set to cancel at period end.");
            setCancelOpen(false);
            setCancelReason("");
            await load();
        } catch (e) { setErr(e.message); }
        finally { setBusy(false); }
    }

    async function handleRefund() {
        setBusy(true); setErr(null);
        try {
            const r = await api.adminForceRefund(userId, refundConfirmWord, refundReason.trim());
            setNotice(`Refunded ${(r.refund_amount_cents / 100).toFixed(2)} USD. Access revoked.`);
            setRefundOpen(false);
            setRefundConfirmWord("");
            setRefundReason("");
            await load();
        } catch (e) { setErr(e.message); }
        finally { setBusy(false); }
    }

    async function handlePhase() {
        setBusy(true); setErr(null);
        try {
            if (phaseDialog.action === "unlock") {
                await api.adminUnlockPhase(userId, phaseDialog.phase_id, phaseReason);
            } else {
                await api.adminLockPhase(userId, phaseDialog.phase_id, phaseReason);
            }
            setNotice(`Phase ${phaseDialog.action}ed.`);
            setPhaseDialog(null);
            setPhaseReason("");
            await load();
        } catch (e) { setErr(e.message); }
        finally { setBusy(false); }
    }

    if (err && !detail) {
        return <div className="p-8 text-sm" style={{ color: "#B42318" }}>{err}</div>;
    }
    if (!detail) return <div className="p-8 text-sm">Loading…</div>;

    const { user, phase_progress, journal, audit } = detail;
    const canRefund =
        ["active", "past_due", "canceled"].includes(user.subscription_status) &&
        !!user.stripe_subscription_id &&
        user.has_access !== false;

    return (
        <section className="max-w-6xl mx-auto px-6 py-10 space-y-6" data-testid="admin-student-detail-page">
            <div className="text-xs" style={{ color: "var(--st-silver-500)" }}>
                <Link to="/admin/students" className="hover:underline inline-flex items-center gap-1">
                    <ArrowLeft size={12} /> All students
                </Link>
            </div>

            <div className="st-surface p-6" data-testid="student-header-card">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <h1 className="font-serif text-3xl" style={{ color: "var(--st-navy)" }}>{user.name || "—"}</h1>
                        <div className="mt-1 text-sm" style={{ color: "var(--st-ink-muted)" }}>{user.email}</div>
                        <div className="mt-1 text-xs" style={{ color: "var(--st-silver-500)" }}>user_id: {user.user_id}</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {["active", "past_due"].includes(user.subscription_status) && (
                            <button
                                type="button"
                                onClick={() => setCancelOpen(true)}
                                className="btn-outline-navy text-sm"
                                data-testid="cancel-sub-btn"
                            >
                                Cancel subscription
                            </button>
                        )}
                        {canRefund && (
                            <button
                                type="button"
                                onClick={() => setRefundOpen(true)}
                                className="text-sm font-semibold rounded-lg px-4 py-2 border"
                                style={{ background: "#FEF3F2", color: "#B42318", borderColor: "#FECDCA" }}
                                data-testid="force-refund-btn"
                            >
                                Force refund…
                            </button>
                        )}
                    </div>
                </div>
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatBadge label="Role" value={user.role} />
                    <StatBadge label="Plan" value={user.plan} />
                    <StatBadge label="Status" value={user.subscription_status || "—"} />
                    <StatBadge label="Verified" value={user.email_verified ? "Yes" : "No"} />
                    <StatBadge label="Joined" value={user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"} />
                    <StatBadge label="Current period end" value={user.current_period_end ? new Date(user.current_period_end).toLocaleDateString() : "—"} />
                    <StatBadge label="Stripe customer" value={user.stripe_customer_id ? user.stripe_customer_id.slice(0, 14) + "…" : "—"} />
                    <StatBadge label="Stripe subscription" value={user.stripe_subscription_id ? user.stripe_subscription_id.slice(0, 14) + "…" : "—"} />
                </div>
            </div>

            {notice && (
                <div className="rounded-lg p-3 text-sm" style={{ background: "#ECFDF3", color: "#067647", border: "1px solid #ABEFC6" }} data-testid="student-notice">
                    {notice}
                </div>
            )}
            {err && (
                <div className="rounded-lg p-3 text-sm" style={{ background: "#FEF3F2", color: "#B42318", border: "1px solid #FECDCA" }} data-testid="student-error">
                    {err}
                </div>
            )}

            {/* Phase progress */}
            <div className="st-surface p-6" data-testid="student-phase-progress">
                <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Curriculum progress</div>
                <ul className="mt-4 divide-y" style={{ borderColor: "var(--st-line)" }}>
                    {phase_progress.map((p) => (
                        <li key={p.phase_id} className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`phase-row-${p.slug}`}>
                            <div>
                                <div className="font-semibold" style={{ color: "var(--st-navy)" }}>{p.name}</div>
                                <div className="text-xs" style={{ color: "var(--st-silver-500)" }}>
                                    {p.unlocked ? (p.completed ? "Completed" : "Unlocked") : "Locked"}
                                    {p.admin_override && " · Admin override"}
                                </div>
                            </div>
                            <div className="flex gap-2">
                                {!p.unlocked && (
                                    <button
                                        type="button"
                                        onClick={() => setPhaseDialog({ phase_id: p.phase_id, name: p.name, action: "unlock" })}
                                        className="btn-outline-navy text-xs inline-flex items-center gap-1"
                                        data-testid={`unlock-phase-${p.slug}`}
                                    >
                                        <Unlock size={12} /> Unlock
                                    </button>
                                )}
                                {p.unlocked && (
                                    <button
                                        type="button"
                                        onClick={() => setPhaseDialog({ phase_id: p.phase_id, name: p.name, action: "lock" })}
                                        className="btn-ghost text-xs inline-flex items-center gap-1"
                                        data-testid={`lock-phase-${p.slug}`}
                                    >
                                        <Lock size={12} /> Lock
                                    </button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                <div className="st-surface p-6" data-testid="student-journal-section">
                    <div className="flex items-center justify-between">
                        <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Journal ({journal.total})</div>
                        {journal.total > 0 && (
                            <Link to="/admin/journal" className="link-royal text-xs">Open journal review →</Link>
                        )}
                    </div>
                    {journal.recent.length === 0 ? (
                        <p className="mt-3 text-sm italic" style={{ color: "var(--st-silver-500)" }}>No entries.</p>
                    ) : (
                        <ul className="mt-3 space-y-2">
                            {journal.recent.map((e) => (
                                <li key={e.id} className="flex items-center justify-between text-sm" data-testid={`recent-entry-${e.id}`}>
                                    <div>
                                        <span className="font-semibold" style={{ color: "var(--st-navy)" }}>{e.ticker}</span>
                                        <span className="mx-2" style={{ color: "var(--st-silver-500)" }}>·</span>
                                        <span className="text-xs" style={{ color: "var(--st-ink-muted)" }}>{new Date(e.entry_date).toLocaleDateString()}</span>
                                    </div>
                                    <div className="text-xs" style={{ color: "var(--st-silver-500)" }}>
                                        {e.outcome} {e.pnl_percent !== null && e.pnl_percent !== undefined && <>· {e.pnl_percent.toFixed(2)}%</>}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="st-surface p-6" data-testid="student-audit-section">
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Admin actions on this student</div>
                    {audit.length === 0 ? (
                        <p className="mt-3 text-sm italic" style={{ color: "var(--st-silver-500)" }}>No admin actions taken.</p>
                    ) : (
                        <ul className="mt-3 space-y-2 text-sm">
                            {audit.map((r, i) => (
                                <li key={i} className="flex items-start justify-between gap-3" data-testid={`audit-item-${i}`}>
                                    <div>
                                        <div className="font-semibold" style={{ color: "var(--st-navy)" }}>{r.action}</div>
                                        {r.detail?.reason && (
                                            <div className="text-xs" style={{ color: "var(--st-ink-muted)" }}>{r.detail.reason}</div>
                                        )}
                                    </div>
                                    <div className="text-xs whitespace-nowrap" style={{ color: "var(--st-silver-500)" }}>
                                        {new Date(r.timestamp).toLocaleDateString()}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* Cancel Sub Dialog */}
            <Dialog open={cancelOpen} onOpenChange={(v) => !v && setCancelOpen(false)}>
                <DialogContent data-testid="cancel-sub-dialog">
                    <DialogHeader>
                        <DialogTitle>Cancel subscription</DialogTitle>
                        <DialogDescription>
                            Softly schedules cancellation at period end. Access continues until{" "}
                            {user.current_period_end ? new Date(user.current_period_end).toLocaleDateString() : "the end of the current period"}.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <label className="st-label">Reason (visible in audit log)</label>
                        <textarea
                            className="st-input"
                            style={{ height: "auto", padding: "0.5rem 0.9rem" }}
                            rows={3}
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            data-testid="cancel-reason-input"
                        />
                    </div>
                    <DialogFooter>
                        <button type="button" className="btn-ghost text-sm" onClick={() => setCancelOpen(false)}>Back</button>
                        <button
                            type="button"
                            className="btn-royal text-sm"
                            disabled={busy || cancelReason.trim().length < 3}
                            onClick={handleCancel}
                            data-testid="cancel-sub-confirm"
                        >
                            {busy ? "Working…" : "Cancel subscription"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Force Refund Dialog */}
            <Dialog open={refundOpen} onOpenChange={(v) => !v && setRefundOpen(false)}>
                <DialogContent data-testid="force-refund-dialog">
                    <DialogHeader>
                        <DialogTitle style={{ color: "#B42318" }} className="flex items-center gap-2">
                            <AlertTriangle size={18} /> Force refund + cancel
                        </DialogTitle>
                        <DialogDescription>
                            This will refund the latest paid invoice on Stripe, immediately cancel the subscription,
                            and revoke curriculum access. This cannot be undone. Type <strong>REFUND</strong> to confirm.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="st-label">Reason (required, logged)</label>
                            <textarea
                                rows={3}
                                className="st-input"
                                style={{ height: "auto", padding: "0.5rem 0.9rem" }}
                                value={refundReason}
                                onChange={(e) => setRefundReason(e.target.value)}
                                data-testid="refund-reason-input"
                            />
                        </div>
                        <div>
                            <label className="st-label">Type REFUND to confirm</label>
                            <input
                                className="st-input"
                                value={refundConfirmWord}
                                onChange={(e) => setRefundConfirmWord(e.target.value)}
                                placeholder="REFUND"
                                data-testid="refund-confirm-word"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <button type="button" className="btn-ghost text-sm" onClick={() => setRefundOpen(false)}>Cancel</button>
                        <button
                            type="button"
                            className="text-sm font-semibold rounded-lg px-4 py-2"
                            style={{
                                background: refundConfirmWord === "REFUND" && refundReason.trim().length >= 3 ? "#B42318" : "var(--st-silver-100)",
                                color: refundConfirmWord === "REFUND" && refundReason.trim().length >= 3 ? "#fff" : "var(--st-silver-500)",
                                cursor: busy ? "wait" : "pointer",
                            }}
                            disabled={busy || refundConfirmWord !== "REFUND" || refundReason.trim().length < 3}
                            onClick={handleRefund}
                            data-testid="force-refund-confirm"
                        >
                            {busy ? "Refunding…" : "Refund & revoke"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Phase unlock/lock dialog */}
            <Dialog open={!!phaseDialog} onOpenChange={(v) => !v && setPhaseDialog(null)}>
                <DialogContent data-testid="phase-override-dialog">
                    <DialogHeader>
                        <DialogTitle>
                            {phaseDialog?.action === "unlock" ? "Unlock" : "Lock"} phase: {phaseDialog?.name}
                        </DialogTitle>
                        <DialogDescription>
                            Reason is required and will appear in the audit log for this student.
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <label className="st-label">Reason</label>
                        <textarea
                            rows={3}
                            className="st-input"
                            style={{ height: "auto", padding: "0.5rem 0.9rem" }}
                            value={phaseReason}
                            onChange={(e) => setPhaseReason(e.target.value)}
                            data-testid="phase-override-reason"
                        />
                    </div>
                    <DialogFooter>
                        <button type="button" className="btn-ghost text-sm" onClick={() => setPhaseDialog(null)}>Cancel</button>
                        <button
                            type="button"
                            className="btn-royal text-sm"
                            disabled={busy || phaseReason.trim().length < 3}
                            onClick={handlePhase}
                            data-testid="phase-override-confirm"
                        >
                            {busy ? "Working…" : (phaseDialog?.action === "unlock" ? "Unlock phase" : "Lock phase")}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </section>
    );
}
